import React, { useState, useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Site1Template } from "../templates";
import { buildSiteData } from "../templates/Site1";
import { GetBookingPageBySlugAction } from "../store/action";
import BookingModal from "./booking/BookingModal";
import { useMyProfile, toBookingPrefill } from "../hooks/useMyProfile";
import { usePageMeta } from "../hooks/usePageMeta";

/**
 * Public Booking Page Component
 * Displays the organization's booking page using the Site1 template
 * Accessible without authentication via /:organizationSlug route
 *
 * The settings → template mapping is `buildSiteData` (templates/Site1/mapSettings.js),
 * shared with Site & widget's preview and the setup wizard's, so what a business
 * sees while editing is decided by the same rules as what its customers see.
 */

const BookingPage = () => {
  const { t } = useTranslation();
  const { organizationSlug } = useParams();
  const [searchParams] = useSearchParams();
  const [bookingPageData, setBookingPageData] = useState(null);
  const [pageMeta, setPageMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // The canonical is the slug the server resolved, not the one in the address
  // bar: `/Frizeria-Colțul` and every `/frizeriacoltul?service=…&time=…` that
  // /discover links to are this same page, and Google has to be told which
  // URL is its own. Nothing is claimed until the page has loaded.
  usePageMeta(pageMeta);

  // Most people here are anonymous, and for them this is a no-op — the hook does
  // not call the API without a session, so `profileLoading` is false from the
  // first render and nothing below waits. But a signed-in customer who followed
  // a link to a business's own page should not be asked to type details their
  // account already holds any more than one who came through the Book tab.
  const { profile, loading: profileLoading } = useMyProfile();

  // A slot chosen on the discovery page arrives in the query string. It opens
  // the modal straight away and pre-fills it: the visitor already picked the
  // day and the hour, and landing on a marketing page that makes them start
  // over is how that choice gets lost.
  const initialSelection = useMemo(() => {
    const timeSlot = searchParams.get("time");
    if (!timeSlot) return null;

    return {
      serviceId: searchParams.get("service"),
      resourceId: searchParams.get("resource"),
      date: searchParams.get("date"),
      timeSlot,
    };
  }, [searchParams]);

  const [isBookingModalOpen, setIsBookingModalOpen] = useState(
    Boolean(initialSelection),
  );

  useEffect(() => {
    if (!organizationSlug) return;

    let cancelled = false;

    const fetchBookingPageData = async () => {
      try {
        setLoading(true);
        const data = await GetBookingPageBySlugAction(organizationSlug);
        if (cancelled) return;

        const siteData = buildSiteData(data, {
          slug: organizationSlug,
          onBook: () => setIsBookingModalOpen(true),
          t,
        });
        setBookingPageData(siteData);
        setPageMeta({
          title: siteData.businessName,
          description: siteData.hero.description,
          path: data?.slug ? `/${data.slug}` : null,
        });
        setError(null);
      } catch (err) {
        if (cancelled) return;
        console.error("Error fetching booking page data:", err);

        if (err.response && err.response.status === 404) {
          window.location.href = window.location.origin;
          return;
        }

        setError(t("BookingPage.error"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchBookingPageData();

    return () => {
      cancelled = true;
    };
    // `t` is a dependency because the mapped labels and the error carry the
    // current language: switching it has to rebuild them, not leave the page
    // half-translated.
  }, [organizationSlug, t]);

  const handleNavigate = (href) => {
    if (href === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // Menu hrefs are anchors ("#about"); getElementById wants the bare id.
    const element = document.getElementById(href.replace(/^#/, ""));
    element?.scrollIntoView({ behavior: "smooth" });
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p>{t("BookingPage.loading")}</p>
      </div>
    );
  }

  // A failed load previously rendered sample barbershop copy under the org's
  // own name; say plainly that the page did not load instead.
  if (error || !bookingPageData) {
    return (
      <div className="flex h-screen items-center justify-center p-6 text-center">
        <p>{error || t("BookingPage.errorShort")}</p>
      </div>
    );
  }

  return (
    <>
      <Site1Template data={bookingPageData} onNavigate={handleNavigate} />
      {/* Held back until the profile question is settled. The modal adopts its
          pre-fill when it opens, and a `?time=` link opens it on the first
          render — so mounting it before the answer arrives would leave a
          signed-in customer with the empty form this exists to avoid. Costs
          anonymous visitors nothing: for them the answer is already in. */}
      {profileLoading ? null : (
        <BookingModal
          isOpen={isBookingModalOpen}
          onClose={() => setIsBookingModalOpen(false)}
          organizationSlug={organizationSlug}
          initialSelection={initialSelection}
          prefillCustomer={toBookingPrefill(profile)}
        />
      )}
    </>
  );
};

export default BookingPage;
